import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FileText, Image, Sparkles, Loader2, Check, X, Plus,
  Trash2, Edit, Save, Upload, ChevronDown, ChevronUp, Lightbulb,
  ClipboardList, MessageSquare, Palette
} from "lucide-react";
import { AppNav } from "@/components/AppNav";
import StoryProgress from "@/components/StoryProgress";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GenerateFromStory() {
  const navigate = useNavigate();
  
  // Input state
  const [userStory, setUserStory] = useState("");
  const [meetingNotes, setMeetingNotes] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [screenshots, setScreenshots] = useState([]); // Array of { file, preview, base64 }
  
  // UI state
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMeetingNotes, setShowMeetingNotes] = useState(false);
  
  // Results state
  const [generatedTests, setGeneratedTests] = useState([]);
  const [analysisSummary, setAnalysisSummary] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [expandedTests, setExpandedTests] = useState(new Set());
  const [editingTest, setEditingTest] = useState(null);

  // File upload handling
  const onDrop = useCallback(async (acceptedFiles) => {
    const newScreenshots = [];
    
    for (const file of acceptedFiles.slice(0, 5 - screenshots.length)) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Only JPEG, PNG, WEBP allowed.`);
        continue;
      }
      
      // Convert to base64
      const base64 = await fileToBase64(file);
      
      newScreenshots.push({
        file,
        preview: URL.createObjectURL(file),
        base64
      });
    }
    
    setScreenshots(prev => [...prev, ...newScreenshots]);
    if (newScreenshots.length > 0) {
      toast.success(`${newScreenshots.length} image(s) added`);
    }
  }, [screenshots.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxFiles: 5,
    disabled: screenshots.length >= 5
  });

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const generateTestCases = async () => {
    if (!userStory.trim()) {
      toast.error("Please enter a user story");
      return;
    }

    setGenerating(true);
    setGeneratedTests([]);
    setAnalysisSummary("");
    setSuggestions([]);

    try {
      const response = await axios.post(`${API}/generate-from-story`, {
        user_story: userStory,
        meeting_notes: meetingNotes || null,
        target_url: targetUrl || null,
        screenshots: screenshots.map(s => s.base64)
      }, { withCredentials: true });

      setGeneratedTests(response.data.test_cases);
      setAnalysisSummary(response.data.analysis_summary);
      setSuggestions(response.data.suggestions);
      
      // Auto-select all tests
      setSelectedTests(new Set(response.data.test_cases.map((_, idx) => idx)));
      
      toast.success(`Generated ${response.data.test_cases.length} test cases!`);
    } catch (error) {
      console.error('Generation error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402) {
        // Quota exceeded or plan inactive — detail may be an object.
        const msg = typeof detail === 'object' ? detail.message : detail;
        toast.error(msg || 'You have used all your free generations this month. Please upgrade.');
      } else {
        toast.error((typeof detail === 'object' ? detail.message : detail) || 'Failed to generate test cases');
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleTestSelection = (index) => {
    setSelectedTests(prev => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  const toggleTestExpand = (index) => {
    setExpandedTests(prev => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  const saveSelectedTests = async () => {
    const testsToSave = generatedTests.filter((_, idx) => selectedTests.has(idx));
    
    if (testsToSave.length === 0) {
      toast.error("Please select at least one test case to save");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/save-generated-tests`, testsToSave, { withCredentials: true });
      toast.success(response.data.message);
      navigate('/dashboard');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save test cases');
    } finally {
      setSaving(false);
    }
  };

  const updateTestCase = (index, field, value) => {
    setGeneratedTests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateTestStep = (testIndex, stepIndex, field, value) => {
    setGeneratedTests(prev => {
      const updated = [...prev];
      const steps = [...updated[testIndex].steps];
      steps[stepIndex] = { ...steps[stepIndex], [field]: value };
      updated[testIndex] = { ...updated[testIndex], steps };
      return updated;
    });
  };

  const addStep = (testIndex) => {
    setGeneratedTests(prev => {
      const updated = [...prev];
      const steps = [...updated[testIndex].steps];
      steps.push({
        step_number: steps.length + 1,
        description: "",
        expected_result: ""
      });
      updated[testIndex] = { ...updated[testIndex], steps };
      return updated;
    });
  };

  const removeStep = (testIndex, stepIndex) => {
    setGeneratedTests(prev => {
      const updated = [...prev];
      const steps = updated[testIndex].steps.filter((_, idx) => idx !== stepIndex);
      // Renumber steps
      steps.forEach((s, idx) => s.step_number = idx + 1);
      updated[testIndex] = { ...updated[testIndex], steps };
      return updated;
    });
  };

  const removeTest = (index) => {
    setGeneratedTests(prev => prev.filter((_, idx) => idx !== index));
    setSelectedTests(prev => {
      const updated = new Set();
      prev.forEach(i => {
        if (i < index) updated.add(i);
        else if (i > index) updated.add(i - 1);
      });
      return updated;
    });
  };

  const hasResults = generatedTests.length > 0;

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="generate-from-story-page">
      <AppNav />

      <div className="px-6 py-8 md:px-10">
        <div className={`mx-auto transition-all duration-300 ${hasResults ? "max-w-screen-xl" : "max-w-3xl"}`}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

            {/* Page header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-[#a855f7] to-[#00b37a] rounded-lg flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Generate from Story
                </h1>
                <p className="text-slate-500 text-sm">AI-powered test case generation from user stories</p>
              </div>
            </div>

            {/* ── RESULTS MODE: full-width layout with collapsed prompt bar ── */}
            {hasResults ? (
              <div className="space-y-4">
                {/* Collapsed prompt bar */}
                <div className="glass-card rounded-xl p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-0.5">Story prompt</p>
                    <p className="text-sm text-slate-700 truncate">{userStory}</p>
                  </div>
                  <button
                    onClick={() => {
                      setGeneratedTests([]);
                      setAnalysisSummary("");
                      setSuggestions([]);
                      setSelectedTests(new Set());
                    }}
                    className="flex items-center gap-1.5 text-sm font-medium text-[#a855f7] hover:text-[#9333ea] bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Edit prompt
                  </button>
                </div>

                {/* Analysis summary */}
                {analysisSummary && (
                  <div className="glass-card rounded-xl p-4 border-l-4 border-[#00b37a] flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-[#00b37a] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-[#00b37a] uppercase tracking-wide mb-1">AI Analysis</p>
                      <p className="text-sm text-slate-700">{analysisSummary}</p>
                    </div>
                  </div>
                )}

                {/* Generated test cases — full width, grid */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      Generated Test Cases
                      <span className="ml-2 text-sm font-normal text-slate-400">({generatedTests.length})</span>
                    </h2>
                    <span className="text-sm text-slate-500">{selectedTests.size} selected</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {generatedTests.map((test, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                        className={`glass-card rounded-xl border-2 transition-all ${
                          selectedTests.has(idx) ? "border-[#00b37a]" : "border-transparent hover:border-slate-200"
                        }`}
                      >
                        <div className="p-4">
                          {/* Card header */}
                          <div className="flex items-start gap-2 mb-3">
                            <Checkbox
                              data-testid={`test-checkbox-${idx}`}
                              checked={selectedTests.has(idx)}
                              onCheckedChange={() => toggleTestSelection(idx)}
                              className="mt-0.5 border-slate-300 data-[state=checked]:bg-[#00b37a] data-[state=checked]:border-[#00b37a]"
                            />
                            <div className="flex-1 min-w-0">
                              {editingTest === idx ? (
                                <Input
                                  autoFocus
                                  value={test.name}
                                  onChange={(e) => updateTestCase(idx, "name", e.target.value)}
                                  onBlur={() => setEditingTest(null)}
                                  onKeyDown={(e) => e.key === "Enter" && setEditingTest(null)}
                                  className="h-7 text-sm font-semibold border-[#00b37a] bg-white px-2 mb-1"
                                />
                              ) : (
                                <p className="text-sm font-semibold text-slate-900 leading-snug mb-0.5">{test.name}</p>
                              )}
                              <p className="text-xs text-slate-500 line-clamp-2">{test.description}</p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                title="Edit name"
                                onClick={() => setEditingTest(editingTest === idx ? null : idx)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                title="Delete"
                                onClick={() => removeTest(idx)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Step count + expand */}
                          <button
                            onClick={() => toggleTestExpand(idx)}
                            className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-800 transition-colors w-full"
                          >
                            <span className="bg-slate-100 border border-slate-200 rounded px-2 py-0.5 font-medium">
                              {test.steps.length} steps
                            </span>
                            {expandedTests.has(idx) ? (
                              <><ChevronUp className="w-3.5 h-3.5" /> Hide steps</>
                            ) : (
                              <><ChevronDown className="w-3.5 h-3.5" /> Show &amp; edit steps</>
                            )}
                          </button>

                          {/* Steps */}
                          <AnimatePresence>
                            {expandedTests.has(idx) && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="mt-3 space-y-2 overflow-hidden"
                              >
                                {test.steps.map((step, stepIdx) => (
                                  <div key={stepIdx} className="bg-slate-50 border border-slate-200 p-2.5 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <span className="text-[#00b37a] font-bold text-xs mt-0.5">{step.step_number}.</span>
                                      <div className="flex-1">
                                        <input
                                          value={step.description}
                                          onChange={(e) => updateTestStep(idx, stepIdx, "description", e.target.value)}
                                          className="w-full text-xs text-slate-800 bg-transparent border-none outline-none"
                                        />
                                        {step.expected_result && (
                                          <p className="text-xs text-slate-400 mt-0.5 italic">→ {step.expected_result}</p>
                                        )}
                                      </div>
                                      {test.steps.length > 1 && (
                                        <button
                                          onClick={() => removeStep(idx, stepIdx)}
                                          className="text-slate-300 hover:text-red-400 flex-shrink-0"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <button
                                  onClick={() => addStep(idx)}
                                  className="text-xs text-[#00b37a] hover:text-[#009966] flex items-center gap-1 font-medium"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add step
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Suggestions */}
                {suggestions.length > 0 && (
                  <div className="glass-card rounded-xl p-4 border-l-4 border-[#a855f7]">
                    <p className="text-xs font-semibold text-[#a855f7] uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <Lightbulb className="w-4 h-4" />
                      Additional suggestions
                    </p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {suggestions.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-[#a855f7] mt-0.5">•</span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Save bar */}
                <div className="glass-card rounded-xl p-4 flex items-center justify-between gap-4 sticky bottom-4 shadow-lg border-[#00b37a]/20">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-900">{selectedTests.size}</span> of {generatedTests.length} test cases selected
                  </p>
                  <Button
                    data-testid="save-all-btn"
                    className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-lg font-semibold px-6"
                    onClick={saveSelectedTests}
                    disabled={saving || selectedTests.size === 0}
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" />Save {selectedTests.size} Test Case{selectedTests.size !== 1 ? "s" : ""} to Dashboard</>
                    )}
                  </Button>
                </div>
              </div>

            ) : (
              /* ── INPUT MODE: centered single column ── */
              <div className="space-y-4">
                {/* Input Section */}
                <div className="space-y-4">
                  {/* User Story Input */}
                  <Card className="glass-card rounded-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        <ClipboardList className="w-5 h-5 mr-2 text-[#00b37a]" />
                        User Story *
                      </CardTitle>
                      <CardDescription className="text-slate-500">
                        Paste your user story with acceptance criteria
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        data-testid="user-story-input"
                        placeholder={`As a [user type], I want to [action] so that [benefit]

Acceptance Criteria:
- Given [context], when [action], then [result]
- User should be able to...
- System should validate...`}
                        className="min-h-[200px] bg-slate-50 border-slate-200 focus:border-[#00b37a] text-slate-900 rounded-lg resize-none"
                        value={userStory}
                        onChange={(e) => setUserStory(e.target.value)}
                      />
                    </CardContent>
                  </Card>

                  {/* Meeting Notes (Collapsible) */}
                  <Card className="glass-card rounded-xl">
                    <CardHeader
                      className="cursor-pointer"
                      onClick={() => setShowMeetingNotes(!showMeetingNotes)}
                    >
                      <CardTitle className="flex items-center justify-between text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        <div className="flex items-center">
                          <MessageSquare className="w-5 h-5 mr-2 text-[#a855f7]" />
                          Meeting Notes
                          <Badge className="ml-2 bg-slate-100 text-slate-500 border-slate-200">Optional</Badge>
                        </div>
                        {showMeetingNotes ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                      </CardTitle>
                    </CardHeader>
                    <AnimatePresence>
                      {showMeetingNotes && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <CardContent>
                            <Textarea
                              data-testid="meeting-notes-input"
                              placeholder="Paste meeting transcripts, notes, or additional context that might help generate better test cases..."
                              className="min-h-[150px] bg-slate-50 border-slate-200 focus:border-[#a855f7] text-slate-900 rounded-lg resize-none"
                              value={meetingNotes}
                              onChange={(e) => setMeetingNotes(e.target.value)}
                            />
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>

                  {/* Figma Screenshots */}
                  <Card className="glass-card rounded-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        <Palette className="w-5 h-5 mr-2 text-sky-500" />
                        UI Screenshots
                        <Badge className="ml-2 bg-slate-100 text-slate-500 border-slate-200">Optional</Badge>
                      </CardTitle>
                      <CardDescription className="text-slate-500">
                        Upload Figma/UI screenshots for better field detection
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                          isDragActive
                            ? 'border-sky-400 bg-sky-50'
                            : screenshots.length >= 5
                              ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
                              : 'border-slate-200 hover:border-slate-400 bg-slate-50 hover:bg-white'
                        }`}
                      >
                        <input {...getInputProps()} data-testid="screenshot-upload" />
                        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragActive ? 'text-sky-500' : 'text-slate-400'}`} />
                        {screenshots.length >= 5 ? (
                          <p className="text-slate-400">Maximum 5 images reached</p>
                        ) : isDragActive ? (
                          <p className="text-sky-500">Drop the images here...</p>
                        ) : (
                          <p className="text-slate-500">
                            Drag &amp; drop images, or click to select<br />
                            <span className="text-xs text-slate-400">JPEG, PNG, WEBP • Max 5 images</span>
                          </p>
                        )}
                      </div>

                      {/* Screenshot Previews */}
                      {screenshots.length > 0 && (
                        <div className="grid grid-cols-5 gap-2 mt-4">
                          {screenshots.map((screenshot, idx) => (
                            <div key={idx} className="relative group">
                              <img
                                src={screenshot.preview}
                                alt={`Screenshot ${idx + 1}`}
                                className="w-full h-16 object-cover rounded-lg border border-slate-200"
                              />
                              <button
                                onClick={() => removeScreenshot(idx)}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Target URL */}
                  <Card className="glass-card rounded-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Target URL
                        <Badge className="ml-2 bg-slate-100 text-slate-500 border-slate-200">Optional</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Input
                        data-testid="target-url-input"
                        placeholder="https://your-app.com"
                        className="bg-slate-50 border-slate-200 focus:border-[#00b37a] text-slate-900 rounded-lg"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                      />
                    </CardContent>
                  </Card>

                  {/* Generate Button */}
                  <Button
                    data-testid="generate-btn"
                    className="w-full bg-gradient-to-r from-[#a855f7] to-[#00b37a] text-white hover:opacity-90 rounded-xl font-semibold py-5 text-base"
                    onClick={generateTestCases}
                    disabled={generating || !userStory.trim()}
                  >
                    {generating ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Analyzing & Generating...</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2" />Generate Test Cases</>
                    )}
                  </Button>

                  {/* Loading state */}
                  {generating && (
                    <StoryProgress active={generating} hasScreenshots={screenshots.length > 0} />
                  )}
                </div>
              </div>
            )}

          </motion.div>
        </div>
      </div>
    </div>
  );
}
